import { createFileRoute, Link } from "@tanstack/react-router";
import { BookOpen, CalendarClock, Clock } from "lucide-react";
import { SiteLayout } from "@/components/layout/SiteLayout";
import { FaqSection } from "@/components/seo/FaqSection";
import { ContentBlock, TableScroll } from "@/components/layout/ContentBlock";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { GUIDES, GUIDES_INDEX_PATH, type GuideMeta } from "@/lib/guides";
import { pageHead, breadcrumbJsonLd, ldScript, absoluteUrl } from "@/lib/seo";

const PATH = GUIDES_INDEX_PATH;
const TITLE = "Barcode Guides — UPC, EAN, GTIN & 2D Explainers";
const DESC =
  "Free barcode guides: UPC vs EAN, what a GTIN is, 1D vs 2D, Code 128 explained, and a field guide to common barcode formats — for people who use them.";

const FAQ = [
  {
    q: "Which Barcode Format Should I Use for Retail Packaging?",
    a: "For consumer-pack retail, almost always an EAN-13 (UPC-A inside the US and Canada) carrying the product's GTIN. If you're also planning for GS1 Sunrise 2027, pair the 1D barcode with a 2D QR Code carrying a GS1 Digital Link URL. Our UPC vs EAN guide unpacks the trade-off, and the 1D vs 2D guide covers the dual-carrier story.",
  },
  {
    q: "What's the Difference Between a GTIN and a Barcode?",
    a: "A GTIN is a number — the Global Trade Item Number that identifies a product. A barcode is a graphical encoding of that number (or other data). The same GTIN can be drawn as UPC-A, EAN-13, ITF-14 or carried inside a 2D Data Matrix; one identity, several wrappers. The What is a GTIN? guide walks through the four GTIN lengths in plain English.",
  },
  {
    q: "Is GS1 Sunrise 2027 Mandatory for My Brand?",
    a: "Not mandatory in the legal sense, but the safe choice today is to start now. GS1 has committed global retail POS to accept 2D barcodes by end of 2027, and most major retailers are already updating their scanners. The 1D vs 2D guide explains what changes at the till and what brands should put on packaging during the dual-carrier interim.",
  },
  {
    q: "Can I Tell a UPC and EAN Apart by Looking at Them?",
    a: "Yes — count the digits printed under the bars. Twelve digits is a UPC-A; thirteen is an EAN-13. They both encode the same kind of GS1 identifier, and modern scanners read either one, but the digit count tells you which length your master data should store. The UPC vs EAN guide goes deeper.",
  },
  {
    q: "Do I Need Different Barcodes for Different Countries?",
    a: "Usually no. A single GTIN works worldwide — what changes is which 1D symbology the artwork prints (UPC-A in North America, EAN-13 elsewhere) and which retail partner specs apply. The Barcode formats field guide lists the symbology you'll meet in each market and which are interchangeable.",
  },
  {
    q: "Are Barcodes Required by Law?",
    a: "Usually not. No general law requires a barcode on consumer goods; retailers and marketplaces require one as a condition of listing a product. Some regulated sectors are different: the US FDA unique device identification rule requires medical device labels to carry the identifier in machine-readable form, and the EU Falsified Medicines Directive requires a 2D Data Matrix on most prescription medicine packs.",
  },
  {
    q: "Are Barcodes Universal?",
    a: "The standards are. EAN-13, Code 128, QR Code and the other common symbologies are defined by ISO/IEC specifications, so a scanner in any country decodes them the same way. A GTIN licensed through GS1 is also unique worldwide. Internal numbers are the exception: a Code 39 asset tag from one company means nothing to another company's systems.",
  },
];

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Barcode Guides", path: PATH },
];

export const Route = createFileRoute("/barcode-guides/")({
  component: GuidesIndexPage,
  head: () => {
    const base = pageHead({
      title: TITLE,
      description: DESC,
      path: PATH,
      ogType: "website",
    });
    return {
      ...base,
      scripts: [
        ldScript({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: TITLE,
          description: DESC,
          url: absoluteUrl(PATH),
          hasPart: GUIDES.map((g) => ({
            "@type": "Article",
            headline: g.title,
            description: g.description,
            url: absoluteUrl(g.path),
            datePublished: g.lastUpdated,
            dateModified: g.lastUpdated,
          })),
        }),
        // ItemList complements the CollectionPage for search engines that
        // prefer an explicit ordered listing — each guide becomes a
        // numbered list item with its absolute URL and display name.
        ldScript({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: TITLE,
          itemListOrder: "https://schema.org/ItemListOrderAscending",
          numberOfItems: GUIDES.length,
          itemListElement: GUIDES.map((g, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: absoluteUrl(g.path),
            name: g.title,
          })),
        }),
        ldScript(breadcrumbJsonLd(CRUMBS)),
      ],
    };
  },
});

function GuidesIndexPage() {
  return (
    <SiteLayout>
      {/* Editorial header — no grey banded background, no "Hub" eyebrow.
       *  Sits directly on the page background so this index visually
       *  separates itself from the tool / hub pages. */}
      <header className="mx-auto max-w-6xl px-4 pt-8 pb-10 md:pt-10">
        <div>
        <Breadcrumbs items={CRUMBS} />
        <div className="mt-6 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
          <BookOpen className="h-3.5 w-3.5" aria-hidden />
          Reading list
        </div>
        <h1 className="mt-3 text-3xl font-semibold tracking-tight md:text-4xl lg:text-5xl">
          Barcode Guides
        </h1>
        <p className="mt-4 max-w-2xl text-base text-foreground/80 md:text-lg md:leading-relaxed">
          Plain-English explainers for the people who actually print, scan or manage barcodes — UPC
          vs EAN, what a GTIN is, 1D vs 2D, Code 128 and a field guide to the formats you&apos;ll
          meet in retail, logistics and healthcare. Each guide links to a free ReadBarcode tool for checking what it explains.
        </p>
        </div>
      </header>

      {/* Reading-list cards: full-width entries with a horizontal divider between
       *  them, large title, dek subtitle, and a meta line with reading time and
       *  last-updated date. Reads like a magazine library, not a tool grid. */}
      <section aria-label="Available guides" className="mx-auto max-w-6xl px-4">
        <ul className="divide-y divide-border border-y border-border">
          {GUIDES.map((g) => (
            <li key={g.slug}>
              <Link
                to={g.path}
                className="group -mx-4 flex flex-col gap-3 px-4 py-7 transition hover:bg-muted/30"
              >
                <GuideMeta g={g} />
                <h2 className="text-2xl font-semibold leading-tight tracking-tight text-foreground group-hover:text-primary md:text-3xl">
                  {g.title}
                </h2>
                <p className="max-w-2xl text-base text-foreground/80">{g.dek}</p>
                <span className="text-sm font-medium text-primary">Read guide →</span>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {/* Brief editorial note + native-details FAQ, in the page's single
       *  #mv-content block. No ToolSeoSections wall. */}
      <section className="mx-auto mt-16 max-w-6xl px-4 min-[1100px]:grid min-[1100px]:grid-cols-[minmax(0,1fr)_300px] min-[1100px]:gap-10">
        <ContentBlock>
          <h2>About This Library</h2>
          <p>
            Each guide is intentionally narrow — one barcode topic, one clear takeaway — and ends
            with the matching ReadBarcode tool so you can act on what you just read. Start with <Link to="/barcode-guides/what-is-a-gtin">What is a GTIN?</Link> if you&apos;re new to product identifiers, skip to <Link to="/barcode-guides/1d-vs-2d-barcodes">1D vs 2D barcodes</Link> for the capacity story, or jump to <Link to="/barcode-guides/sunrise-2027-explained">Sunrise 2027 explained</Link> if you&apos;re planning your 2D transition.
          </p>
          <h2>Which Barcode Should I Use?</h2>
          <p>
            Use the barcode the party scanning it expects: EAN-13 or UPC-A at retail checkout, ITF-14 or GS1-128 on cartons and pallets, and a QR Code when the scan should open a web page.
          </p>
          <TableScroll>
            <table>
              <caption className="sr-only">
                Common barcode jobs, the barcode each one calls for, and the guide that explains it.
              </caption>
              <thead>
                <tr>
                  <th scope="col">Job</th>
                  <th scope="col">Barcode</th>
                  <th scope="col">Guide</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="row">Product sold at a store checkout</th>
                  <td>UPC-A in the US and Canada, EAN-13 elsewhere</td>
                  <td><Link to="/barcode-guides/upc-vs-ean">UPC vs EAN compared</Link></td>
                </tr>
                <tr>
                  <th scope="row">Outer case of a trade item</th>
                  <td>ITF-14 or GS1-128 carrying a GTIN-14</td>
                  <td><Link to="/barcode-guides/what-is-a-gtin">GTIN lengths explained</Link></td>
                </tr>
                <tr>
                  <th scope="row">Pallet or shipment label</th>
                  <td>GS1-128 carrying an SSCC in AI (00)</td>
                  <td><Link to="/barcode-guides/code-128-explained">Code 128 and GS1-128</Link></td>
                </tr>
                <tr>
                  <th scope="row">Internal assets and stock bins</th>
                  <td>Code 128 or Code 39 with your own numbers</td>
                  <td><Link to="/barcode-guides/barcode-formats">Barcode formats field guide</Link></td>
                </tr>
                <tr>
                  <th scope="row">Batch, expiry or a web link on the pack</th>
                  <td>QR Code with a GS1 Digital Link URI, or GS1 DataMatrix</td>
                  <td><Link to="/barcode-guides/gs1-digital-link">GS1 Digital Link guide</Link></td>
                </tr>
              </tbody>
            </table>
          </TableScroll>
          <p>
            For anything that goes to a retailer, marketplace or carrier, follow the barcode guidelines in that partner&apos;s supplier manual.
          </p>
          <h3>Barcode Size Guide</h3>
          <p>
            Size is set by the narrowest bar or module, called the X-dimension. For EAN-13 and UPC-A at retail checkout, the GS1 General Specifications, the reference GS1 barcode guide, set the target X-dimension at 0.330 mm, the 100% size, with an allowed range from 0.264 mm to 0.660 mm, or 80% to 200%.
          </p>
          <p>
            Scale the whole symbol rather than squashing it. Cutting the bar height, known as truncation, saves space but makes the code harder to scan at a checkout, and GS1 advises against it on retail items.
          </p>
          <p>
            Keep the blank margin around the symbol as well:
          </p>
          <ul>
            <li>Code 128 and GS1-128: at least 10 times the X-dimension on each side.</li>
            <li>QR Code: at least four modules on every side.</li>
            <li>Data Matrix: at least one module on every side.</li>
          </ul>
          <h2>Who Controls Barcode Numbers?</h2>
          <p>
            GS1, a not-for-profit standards organization, controls the numbers used on retail barcodes. Its national member organizations license company prefixes to businesses, and each business then assigns its own GTINs from that prefix.
          </p>
          <p>
            The symbologies themselves are published ISO/IEC standards that anyone may use without a license, whether for a Code 128 or a QR Code. What is managed is the number inside the barcode, and only for identifiers meant to be unique across trading partners.
          </p>
          <ul>
            <li>GTINs, SSCCs and GLNs come from a GS1 Company Prefix licensed by a GS1 member organization such as GS1 US.</li>
            <li>ISBNs come from the national ISBN agency, and an ISBN-13 starting 978 or 979 doubles as an EAN-13.</li>
            <li>Asset tags, bin labels and other internal numbers belong to whoever prints them.</li>
          </ul>
          <p>
            The first digits of a GTIN show which GS1 member organization issued the prefix, not where the product was made.
          </p>
          <FaqSection heading="Frequently Asked Questions" items={FAQ} />
        </ContentBlock>
        <aside className="hidden min-[1100px]:block">
          <div id="mv-sidebar" />
        </aside>
      </section>
      {/* Where Journey's sidebar ad rail stops (see ToolLayout). */}
      <div id="mv-sidebar-stop" className="site-footer" aria-hidden="true" />

      <div className="h-16" />
    </SiteLayout>
  );
}

function GuideMeta({ g }: { g: GuideMeta }) {
  const updated = formatDate(g.lastUpdated);
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
      <span className="inline-flex items-center gap-1.5">
        <Clock className="h-3.5 w-3.5" aria-hidden /> {g.readingMinutes} min read
      </span>
      {updated && (
        <span className="inline-flex items-center gap-1.5">
          <CalendarClock className="h-3.5 w-3.5" aria-hidden /> Published {updated}
        </span>
      )}
    </div>
  );
}

function formatDate(iso: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" });
}
